import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ArrowLeftRight,
    Check,
    Copy,
    Eye,
    Palette,
    Pencil,
    Sparkles,
    Trash2,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

import { NodeShell } from './NodeShell';
import { CanvasContextMenu, CsMenuItem } from './CanvasContextMenu';

export interface ProductNodeProduct {
    uuid: string;
    name: string;
    thumbnailPath?: string;
    /** 'surface' | 'model3d' | 'image_only' — drives which recipes apply. */
    visualizationType?: string;
    /** e.g. ["floor"], ["wall"], ["floor", "wall"] — filters Surface picker options. */
    applicationTypes?: string[];
    /**
     * GLB / GLTF 3D model URL for model3d products. Used by the Product
     * Photography recipe's inline rotation widget to render a pose
     * reference that drives deterministic camera angle in generation.
     */
    modelPath?: string;
    /** Largest real dimension in metres, from the product's size — scales the GLB
     *  to its true size when it is placed in a room. */
    largestDimensionM?: number;
    /** Texture file URL for the 3D model (if the GLB doesn't embed it). */
    texturePath?: string;
    /**
     * The photograph a model3d product was built from.
     *
     * For furniture it is usually the ONLY image there is — measured on the
     * demo catalogue, all 357 furniture products carry this and none carry a
     * thumbnail or gallery image. So a feature that needs a product picture
     * (the moodboard composer) has to read it here or find nothing at all.
     */
    originalImagePath?: string;
    /** Seamless material texture for surface products (tile/plank) — drives the
     *  Surface 3D Studio. */
    surfaceTexturePath?: string;
    /** Top-level category slug (e.g. 'rugs') — flags flat floor coverings so
     *  the 3D Studio lays the model flat on the floor. */
    category?: string;
    /**
     * Additional product gallery images (angle / lifestyle / detail
     * shots). Rendered as small clickable cards under the node — clicking
     * one swaps which image the node displays as its main thumbnail.
     * Also forwarded to the worker as additional reference images for
     * multi-view generation.
     */
    galleryImages?: string[];
    /**
     * Index into `galleryImages` of the currently-highlighted thumbnail
     * (or `-1` when the default `thumbnailPath` is showing). Persists on
     * the canvas node so the choice survives page reloads.
     */
    selectedGalleryIndex?: number;
    /**
     * Indexes into `galleryImages` that the vendor has explicitly
     * excluded from being sent as generation references. The default
     * thumbnail (-1) is never excluded. Default: [] (all included).
     */
    excludedGalleryIndexes?: number[];
}

interface ProductNodeProps {
    nodeId: string;
    x: number;
    y: number;
    products: ProductNodeProduct[];
    selected?: boolean;
    zoom: number;
    onDrag: (deltaX: number, deltaY: number) => void;
    onClick?: (modifiers: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }) => void;
    onAddRecipe?: () => void;
    onReplace?: () => void;
    onDuplicate?: () => void;
    onRemove?: () => void;
    /**
     * Fires when the user clicks a gallery thumbnail under the node.
     * Index is -1 for the product's default thumbnail; 0..N-1 for an
     * entry in galleryImages. Parent persists the selection on the
     * canvas node and passes it through on the next generation.
     */
    onSelectGalleryImage?: (productUuid: string, index: number) => void;
    /**
     * Toggles whether a gallery image is INCLUDED as a reference for
     * generations. Called when the vendor clicks the small exclude
     * button that appears on each gallery thumbnail.
     */
    onToggleGalleryExclusion?: (productUuid: string, index: number) => void;
    /** Faded / non-interactive rendering in workspace select mode. */
    dimmed?: boolean;
    /** True while this node's recipe picker is open — keeps the "+" visible. */
    addOpen?: boolean;
}

const NODE_WIDTH = 200;

// The 30px picker tile is drawn five times in this file (group previews, the
// default gallery slot, each gallery image, and the two "+N" chips), so its box
// lives in one place rather than being copied per call site.
const THUMB_TILE = 'size-[30px] shrink-0 rounded-md border-2 bg-muted bg-cover bg-center transition-all';

/** The "+N" chip closing a truncated thumbnail row. */
const OverflowTile: React.FC<{ count: number }> = ({ count }) => (
    <div className={cn(THUMB_TILE, 'flex items-center justify-center rounded-lg border border-border text-xs font-medium text-muted-foreground')}>
        +{count}
    </div>
);

/**
 * A Product node displays one or more products grouped together.
 * Single product → large thumbnail + name. Group → stacked/offset
 * thumbnails + count badge.
 *
 * On hover, reveals an "Add recipe" pill on the right (NodeShell
 * hoverAffordance slot). Clicking it emits onAddRecipe().
 */
export const ProductNode: React.FC<ProductNodeProps> = ({
    nodeId,
    x,
    y,
    products,
    selected,
    zoom,
    onDrag,
    onClick,
    onAddRecipe,
    onReplace,
    onDuplicate,
    onRemove,
    onSelectGalleryImage,
    onToggleGalleryExclusion,
    dimmed,
    addOpen,
}) => {
    const { t } = useTranslation();
    const isGroup = products.length > 1;
    const [previewIndex, setPreviewIndex] = useState(0);
    const previewed = products[previewIndex] || products[0];
    // Displayed thumbnail — gallery selection takes precedence over the
    // product's default thumbnailPath so clicking a gallery card below
    // actually swaps what the main image shows.
    const gallery = previewed?.galleryImages || [];
    const galleryIdx = typeof previewed?.selectedGalleryIndex === 'number'
        ? previewed.selectedGalleryIndex
        : -1;
    const displayedThumbnail =
        galleryIdx >= 0 && gallery[galleryIdx]
            ? gallery[galleryIdx]
            : previewed?.thumbnailPath;
    // Right-click opens a dropdown with the SAME actions as the hover
    // affordance pills. Both coexist — vendors who linger discover the
    // pills, vendors in a hurry hit right-click. The pills are the
    // persistent discoverable surface; the menu is the shortcut.
    const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
    // Full-image preview (eye icon) — lets the vendor compare the source product
    // photo against the generated result.
    const [previewOpen, setPreviewOpen] = useState(false);

    const menuItems: CsMenuItem[] = [];
    if (onAddRecipe) {
        menuItems.push({
            icon: <SvgRecipe />,
            label: t('CONTENT_STUDIO.ACTION_ADD_RECIPE'),
            onClick: onAddRecipe,
        });
    }
    if (onReplace) {
        menuItems.push({
            icon: <SvgReplace />,
            label: t('CONTENT_STUDIO.ACTION_REPLACE'),
            onClick: onReplace,
        });
    }
    if (onDuplicate) {
        menuItems.push({
            icon: <SvgDuplicate />,
            label: t('CONTENT_STUDIO.ACTION_DUPLICATE'),
            onClick: onDuplicate,
        });
    }
    if (onRemove) {
        menuItems.push({
            icon: <SvgRemove />,
            label: t('CONTENT_STUDIO.ACTION_REMOVE'),
            onClick: onRemove,
            destructive: true,
            dividerBefore: menuItems.length > 0,
        });
    }

    return (
      <>
        <NodeShell
            nodeId={nodeId}
            x={x}
            y={y}
            width={NODE_WIDTH}
            zoom={zoom}
            selected={selected}
            accent="var(--foreground)"
            dimmed={dimmed}
            // The node's top label shows the product's own name (falling back
            // to the generic "Product" tag when a name is missing). Long names
            // ellipsize inside NodeShell's label row.
            label={previewed?.name || t('CONTENT_STUDIO.NODE_PRODUCT')}
            onDrag={onDrag}
            onClick={onClick}
            onContextMenu={(e) => {
                if (menuItems.length === 0) return;
                e.preventDefault();
                e.stopPropagation();
                setMenuPos({ x: e.clientX, y: e.clientY });
            }}
            // Hover shows a "+" handle; clicking it opens the recipe picker
            // directly (the only "next step" action). Node ops (Replace/
            // Duplicate/Remove) live in the bottom hover buttons instead.
            addHandle
            onAddClick={onAddRecipe}
            addOpen={addOpen}
            // Quick node ops — circular icon buttons that slide up from the
            // bottom of the node on hover. Remove confirms in place (red).
            bottomAffordance={
                <div className="flex gap-1.5">
                    {displayedThumbnail && (
                        <CircleActionButton
                            title={t('CONTENT_STUDIO.PREVIEW_IMAGE', 'Preview image')}
                            icon={<SvgEye />}
                            onClick={() => setPreviewOpen(true)}
                        />
                    )}
                    {previewed?.uuid && (
                        <CircleActionButton
                            title={t('CONTENT_STUDIO.EDIT_PRODUCT', 'Edit product (new tab)')}
                            icon={<SvgEdit />}
                            onClick={() => window.open(`/products/${previewed.uuid}/edit`, '_blank', 'noopener,noreferrer')}
                        />
                    )}
                    {onReplace && (
                        <CircleActionButton title={t('CONTENT_STUDIO.ACTION_REPLACE')} icon={<SvgReplace />} onClick={onReplace} />
                    )}
                    {onDuplicate && (
                        <CircleActionButton title={t('CONTENT_STUDIO.ACTION_DUPLICATE')} icon={<SvgDuplicate />} onClick={onDuplicate} />
                    )}
                    {onRemove && (
                        <CircleActionButton title={t('CONTENT_STUDIO.ACTION_REMOVE')} icon={<SvgRemove />} onClick={onRemove} destructive />
                    )}
                </div>
            }
        >
            {/* Thumbnail area */}
            <div
                className="relative aspect-square bg-muted bg-cover bg-center"
                style={displayedThumbnail ? { backgroundImage: `url(${displayedThumbnail})` } : undefined}
            >
                {!displayedThumbnail && (
                    <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                        No image
                    </div>
                )}
                {isGroup && (
                    <span className="absolute end-2 top-2 bg-foreground/90 px-2 py-0.5 text-xs font-medium tracking-[0.02em] text-background backdrop-blur-sm">
                        {products.length} grouped
                    </span>
                )}
            </div>

            {/* Compact counter for groups — single products show nothing here. */}
            {isGroup && (
                <div className="px-4 pt-2 pb-1 text-xs text-muted-foreground">
                    {previewIndex + 1} / {products.length}
                </div>
            )}

            {/* Thumbnails row for groups — click to preview */}
            {isGroup && (
                <div className="flex flex-wrap gap-1 px-3 pb-3">
                    {products.slice(0, 6).map((p, i) => (
                        <div
                            key={p.uuid}
                            onClick={(e) => {
                                e.stopPropagation();
                                setPreviewIndex(i);
                            }}
                            className={cn(
                                THUMB_TILE,
                                'cursor-pointer hover:opacity-100',
                                previewIndex === i
                                    ? 'border-foreground opacity-100'
                                    : 'border-border opacity-70 hover:border-input',
                            )}
                            style={p.thumbnailPath ? { backgroundImage: `url(${p.thumbnailPath})` } : undefined}
                        />
                    ))}
                    {products.length > 6 && <OverflowTile count={products.length - 6} />}
                </div>
            )}

            {/* Gallery thumbnails — single products with extra images.
                The first card is the product's default thumbnail; the
                rest are entries from galleryImages. Click to swap which
                image the node displays as its main thumbnail; the
                selection rides through into generations as an extra
                reference image. */}
            {!isGroup && gallery.length > 0 && previewed && (
                <div className="flex flex-wrap gap-1 px-3 pt-1 pb-3">
                    {/* Default thumbnail slot (index = -1) */}
                    {previewed.thumbnailPath && (
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                onSelectGalleryImage?.(previewed.uuid, -1);
                            }}
                            className={cn(
                                THUMB_TILE,
                                'cursor-pointer hover:opacity-100',
                                galleryIdx === -1
                                    ? 'border-foreground opacity-100'
                                    : 'border-border opacity-70 hover:border-input',
                            )}
                            style={{ backgroundImage: `url(${previewed.thumbnailPath})` }}
                        />
                    )}
                    {gallery.slice(0, 8).map((url, i) => {
                        const isExcluded = (previewed.excludedGalleryIndexes || []).includes(i);
                        const isSelectedIdx = galleryIdx === i;
                        return (
                            <div
                                key={`${url}-${i}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (isExcluded) return; // excluded thumbs aren't selectable
                                    onSelectGalleryImage?.(previewed.uuid, i);
                                }}
                                className={cn(
                                    THUMB_TILE,
                                    'group/thumb relative',
                                    isExcluded
                                        ? 'cursor-default border-border opacity-35 grayscale hover:opacity-45'
                                        : isSelectedIdx
                                        ? 'cursor-pointer border-foreground opacity-100'
                                        : 'cursor-pointer border-border opacity-70 hover:border-input hover:opacity-100',
                                )}
                                style={{ backgroundImage: `url(${url})` }}
                            >
                                {isExcluded && (
                                    // Diagonal strikethrough for excluded state.
                                    <div
                                        className="pointer-events-none absolute inset-0"
                                        style={{
                                            background: 'linear-gradient(to top right, transparent calc(50% - 1px), var(--muted-foreground) 50%, transparent calc(50% + 1px))',
                                        }}
                                    />
                                )}
                                {onToggleGalleryExclusion && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onToggleGalleryExclusion(previewed.uuid, i);
                                        }}
                                        title={isExcluded ? t('CONTENT_STUDIO.PRODUCT_INCLUDE_REF') : t('CONTENT_STUDIO.PRODUCT_EXCLUDE_REF')}
                                        className={cn(
                                            'absolute -top-[5px] -right-[5px] flex size-3.5 items-center justify-center rounded-full text-[9px] leading-none font-medium text-background transition-all hover:scale-115',
                                            isExcluded
                                                ? 'bg-success opacity-100'
                                                : 'bg-foreground opacity-0 group-hover/thumb:opacity-100',
                                        )}
                                    >
                                        {isExcluded ? '+' : '×'}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    {gallery.length > 8 && <OverflowTile count={gallery.length - 8} />}
                </div>
            )}
        </NodeShell>
        <CanvasContextMenu
            position={menuPos}
            onClose={() => setMenuPos(null)}
            items={menuItems}
        />
        {/* Full-image preview lightbox. */}
        <Dialog open={previewOpen} onOpenChange={(next) => { if (!next) setPreviewOpen(false); }}>
            <DialogContent
                showCloseButton={false}
                className="w-auto max-w-none bg-transparent p-0 ring-0 sm:max-w-none"
            >
                <DialogTitle className="sr-only">{t('CONTENT_STUDIO.PREVIEW_IMAGE')}</DialogTitle>
                <div
                    onClick={() => setPreviewOpen(false)}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="flex cursor-zoom-out flex-col items-center gap-1"
                >
                    {displayedThumbnail && (
                        <img
                            src={displayedThumbnail}
                            alt={previewed?.name || 'Product'}
                            className="max-h-[85vh] max-w-[88vw] bg-background object-contain"
                        />
                    )}
                    {previewed?.name && (
                        <span className="bg-background/90 px-2 py-0.5 text-sm font-medium text-foreground backdrop-blur-sm">
                            {previewed.name}
                        </span>
                    )}
                </div>
            </DialogContent>
        </Dialog>
      </>
    );
};

ProductNode.displayName = 'ProductNode';

export const PRODUCT_NODE_WIDTH = NODE_WIDTH;

// ─── Shared icons for action pills ──────────────────────────────────
//
// Named wrappers rather than raw lucide imports: every node in this folder
// renders them through CircleActionButton / ActionPill, which size them, and
// the names keep those call sites reading as intent rather than glyph.

export const SvgEye: React.FC = () => <Eye />;

export const SvgRecipe = () => <Palette />;

export const SvgReplace = () => <ArrowLeftRight />;

export const SvgDuplicate = () => <Copy />;

/** A spark — "hand this to the agent". */
export const SvgAgent = () => <Sparkles />;

export const SvgRemove = () => <Trash2 />;

export const SvgCheck = () => <Check />;

export const SvgEdit = () => <Pencil />;

// ─── Circular quick-action button (bottom hover affordance) ─────────────

interface CircleActionButtonProps {
    icon: React.ReactNode;
    title: string;
    onClick: () => void;
    destructive?: boolean;
}

/**
 * Small circular icon button used in a node's bottom hover affordance
 * (Replace / Duplicate / Remove). Self-contained: destructive buttons arm on
 * the first click (outlined red, icon → check, title → "Confirm?") and commit
 * on the second; a click anywhere outside disarms. Non-destructive fire at once.
 */
export const CircleActionButton: React.FC<CircleActionButtonProps> = ({ icon, title, onClick, destructive }) => {
    const { t } = useTranslation();
    const [armed, setArmed] = useState(false);
    const ref = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        if (!armed) return;
        const onDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setArmed(false);
        };
        document.addEventListener('mousedown', onDown, true);
        return () => document.removeEventListener('mousedown', onDown, true);
    }, [armed]);
    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (destructive && !armed) { setArmed(true); return; }
        setArmed(false);
        onClick();
    };
    return (
        <Button
            ref={ref}
            variant="outline"
            size="icon"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={handleClick}
            title={armed ? t('CONTENT_STUDIO.ACTION_CONFIRM') : title}
            aria-label={armed ? t('CONTENT_STUDIO.ACTION_CONFIRM') : title}
            className={cn(
                'size-[30px] rounded-full [&_svg]:size-[15px]',
                destructive || armed
                    ? 'border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive'
                    : 'text-muted-foreground hover:text-foreground',
                armed && 'border-destructive bg-destructive/10',
            )}
        >
            {destructive && armed ? <SvgCheck /> : icon}
        </Button>
    );
};

// ─── Shared action pill (used by all node types) ────────────────────────

interface ActionPillProps {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    destructive?: boolean;
}

/**
 * Shared action pill. Destructive pills (Remove) adopt a click-to-confirm
 * pattern: first click arms the action and re-labels the pill "Confirm?"
 * in an outlined-red state for 2.5s; a second click within that window
 * commits. Avoids a modal dialog while still preventing accidental
 * single-click deletions. Non-destructive pills fire immediately.
 */
export const ActionPill: React.FC<ActionPillProps> = ({ label, icon, onClick, destructive }) => {
    const { t } = useTranslation();
    const [confirming, setConfirming] = React.useState(false);
    const armedTimerRef = React.useRef<number | null>(null);

    // Clear any pending timer on unmount to avoid setting state on a
    // removed node (happens when confirm commits and the node is
    // unmounted before the 2.5s auto-cancel fires).
    React.useEffect(() => () => {
        if (armedTimerRef.current) window.clearTimeout(armedTimerRef.current);
    }, []);

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!destructive) {
            onClick();
            return;
        }
        if (!confirming) {
            setConfirming(true);
            if (armedTimerRef.current) window.clearTimeout(armedTimerRef.current);
            armedTimerRef.current = window.setTimeout(() => {
                setConfirming(false);
                armedTimerRef.current = null;
            }, 2500);
            return;
        }
        // Second click — commit and let the parent unmount / refresh us.
        if (armedTimerRef.current) {
            window.clearTimeout(armedTimerRef.current);
            armedTimerRef.current = null;
        }
        setConfirming(false);
        onClick();
    };

    const armed = destructive && confirming;
    return (
        <Button
            variant="outline"
            onClick={handleClick}
            className={cn(
                'h-auto gap-1.5 px-3.5 py-[7px] text-sm whitespace-nowrap [&_svg]:size-[15px]',
                destructive && 'border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive',
                armed && 'border-destructive bg-destructive/10',
            )}
        >
            {icon}
            {armed ? t('CONTENT_STUDIO.ACTION_CONFIRM') : label}
        </Button>
    );
};
